import { createSelector } from "@reduxjs/toolkit"
import { get, memoize } from "nodash"
import {
    selectNonArchivedOrthoFlights,
    selectOrthoFlightById,
    selectRawOrthoImagesByFlightId,
} from "../../flights/selectors"
import {
    GenericUpload,
    ID,
    ImagePreviewType,
    ImageProcessingState,
    ImageWarnings,
    isUsable,
    OrthoImageWithContext,
    UploadStateSummary,
} from "@nova/constants"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import {
    OrthoState,
    selectAllUploads,
    selectUploadsForSet as selectOrthoUploadsForSet,
} from "plugins/Orthomosaic/OrthomosaicSlice"
import store from "integrations/redux/store"
import { orthoWarningProcessor } from "plugins/Orthomosaic/OrthomosaicWarnings"
import { getFileNameFromImageId, UploadState } from "@nova/slice-multifile-upload"
import { orthoImage, rgbDownscaledImagePath, sampleOrthoImage } from "@nova/util-s3"
import { selectUploadsForSet } from "plugins/ColorFinder/VideoColorFinder/ColorFinderSlice"

const selectOrthoFlightDemoIdByFlightId = memoize((id: string) =>
    createSelector(selectOrthoFlightById(id), (flight) => flight?.demoId)
)

export const selectOrthoImagesByFlightId = memoize((id: string) =>
    createSelector(
        [
            selectRawOrthoImagesByFlightId(id),
            selectActiveWorkspaceId(),
            selectActivatedFireId,
            selectOrthoFlightDemoIdByFlightId(id),
        ],
        (images, orgId, projectId, demoId) => {
            if (!orgId || !projectId) {
                return []
            }

            const isDemo = !!demoId

            return images.map((image) => {
                return {
                    ...image,
                    s3Key: isDemo
                        ? sampleOrthoImage(demoId, image.id)
                        : orthoImage(orgId, projectId, id, image.id),
                    S3KeyLR: image.hasLowQuality
                        ? rgbDownscaledImagePath(orgId, projectId, id, image.id)
                        : undefined,
                    flightId: id,
                    fileName: getFileNameFromImageId(image.id),
                    state: ImageProcessingState.IngestDone,
                }
            }) as OrthoImageWithContext[]
        }
    )
)

export const selectOrthoImagesContextMap = createSelector(
    [selectNonArchivedOrthoFlights],
    (flights) => {
        const state = store.getState()
        const imageMap = {} as Record<string, OrthoImageWithContext>
        flights.forEach((flight) => {
            selectOrthoImagesByFlightId(flight.id)(state).forEach(
                (image) => (imageMap[image.id] = image)
            )
        })
        return imageMap
    }
)

export const selectOrthoImageById = memoize((id: string) =>
    createSelector([selectAllUploads(), selectOrthoImagesContextMap], (uploads, contextMap) => {
        const upload = uploads[id]
        const cached = contextMap[id]
        if (cached) {
            if (!upload) return cached
            const uploadError = get(upload, "error")
            return {
                ...cached,
                state: get(upload, "state"),
                ...(uploadError !== undefined ? { error: uploadError } : {}),
            }
        }
        return upload
    })
)

const mergeImagesAndUploads = (
    images: OrthoImageWithContext[],
    uploads: UploadState<OrthoState>[] | undefined
): (UploadState<OrthoState> | OrthoImageWithContext)[] => {
    const uploadById = new Map(uploads?.map((u) => [u.id, u]))
    const res = [] as (UploadState<OrthoState> | OrthoImageWithContext)[]
    images.forEach((image) => {
        const activeUpload = uploadById.get(image.id)
        if (activeUpload) {
            const uploadError = get(activeUpload, "error")
            const uploadState = get(activeUpload, "state")
            // Active upload session: use upload-slice state so entity-cache websocket
            // updates don't prematurely show IngestDone before the ingest signal fires
            res.push({
                ...image,
                state: uploadState,
                ...(uploadError !== undefined ? { error: uploadError } : {}),
            } as unknown as OrthoImageWithContext)
            uploadById.delete(image.id)
        } else {
            // Refresh: image is entity-cached with no active upload, ingest is done
            res.push({ ...image, state: ImageProcessingState.IngestDone })
        }
    })
    uploadById.forEach((upload) => res.push(upload))
    return res
}

export const selectOrthoImagesAndUploadsByFlightId = memoize((id: string) =>
    createSelector(
        [selectOrthoImagesByFlightId(id), selectOrthoUploadsForSet(id)],
        (images, uploads) => mergeImagesAndUploads(images, uploads)
    )
)

export const selectOrthoImagesAndUploadsWithContextByFlightId = memoize((id: string) =>
    createSelector(
        [selectOrthoImagesByFlightId(id), selectOrthoUploadsForSet(id)],
        (images, uploads) => {
            let imageColors: Record<ID, string> = {}
            if (
                uploads?.every(
                    (upload) =>
                        (upload as GenericUpload).state === ImageProcessingState.ProcessingDone
                )
            ) {
                imageColors = orthoWarningProcessor(id, uploads as GenericUpload[])
            }
            return mergeImagesAndUploads(images, uploads).map((upload) => ({
                ...upload,
                warningColor: imageColors[upload.id],
                imagePreviewType: ImagePreviewType.Ortho,
            }))
        }
    )
)

export const selectSummaryFromOrthoUploads = memoize((id: string) =>
    createSelector([selectOrthoImagesAndUploadsByFlightId(id)], (uploads) => {
        const summary: UploadStateSummary = {
            stateCount: {} as Record<ImageProcessingState, number>,
            uploadCount: uploads?.length || 0,
            errorCount: 0,
            warnings: {} as ImageWarnings,
        }
        uploads?.forEach((upload) => {
            const usable = isUsable(upload)
            if (!usable) return summary.errorCount++
            const state = "state" in usable ? usable.state : ImageProcessingState.UploadDone
            summary.stateCount[state] = ++summary.stateCount[state] || 1
        })
        return summary
    })
)

export const selectOrthoUploadSize = memoize((id: string) =>
    createSelector([selectOrthoImagesAndUploadsByFlightId(id)], (uploads) => {
        let totalSize = 0
        uploads?.forEach((upload) => {
            const size = get(upload, "size", 0)
            totalSize += size
        })
        return totalSize
    })
)
export const selectColorFinderUploadSize = memoize((id: string) =>
    createSelector([selectUploadsForSet(id)], (uploads = []) => {
        return uploads.reduce((acc, curr) => {
            const file = get(curr, "file")
            const size = get(file, "size", 0)
            return acc + size
        }, 0)
    })
)

export const selectOrthoFinishedUploadSize = memoize((id: string) =>
    createSelector([selectOrthoImagesAndUploadsByFlightId(id)], (uploads) => {
        let totalSize = 0
        uploads?.forEach((upload) => {
            const state = get(upload, "state")
            const size = get(upload, "size", 0)
            if (state !== ImageProcessingState.IngestDone) return
            totalSize += size
        })
        return totalSize
    })
)

export const selectIsOrthoFlightUploading = memoize((id: string) =>
    createSelector([selectSummaryFromOrthoUploads(id)], (summary) => {
        return (
            !!summary.stateCount?.[ImageProcessingState.PendingUpload] ||
            !!summary.stateCount?.[ImageProcessingState.Uploading] ||
            !!summary.stateCount?.[ImageProcessingState.UploadDone]
        )
    })
)
